/**
 * The care numbers of one animal that the owner may tune from the admin panel (docs/plans/016-care-curves-admin.md): the meter curves and
 * how long each care action takes to refill its meter. One validator is shared by the server, the admin app and the game, so a bad
 * override can never reach a pet: it keeps the spec's rules (a 1% point of at least 20 hours, ordered phases).
 */
import { MIN_ONE_PERCENT_AT, type CurveProfile } from "./curve";
import { METER_IDS, type MeterId } from "./types";

/** The animals the game has today. */
export const KNOWN_SPECIES: readonly string[] = ["shark"];

/** A meter never lasts more than three effective days before the last chance, so a bad value cannot make a week effortless. */
export const MAX_ONE_PERCENT_AT = 72;
/** Each phase is at least this many hours long (6 minutes). */
export const MIN_PHASE_HOURS = 0.1;
export const MIN_REFILL_SECONDS = 5;
export const MAX_REFILL_SECONDS = 600;

/** How long each care action takes to refill its meter (docs/SPEC.md section 3, a proposal to tune). Rest is a five minute sleep. */
export const DEFAULT_REFILL_SECONDS: Readonly<Record<MeterId, number>> = Object.freeze({ food: 30, fun: 60, clean: 30, love: 20, rest: 300 });

/** The refill times a newly hatched pet of this animal gets, in milliseconds (the unit of the log). */
export function animalRefillsMs(species: string): Record<MeterId, number> {
  const seconds = REFILL_SECONDS_BY_SPECIES[species] ?? DEFAULT_REFILL_SECONDS;
  const out = {} as Record<MeterId, number>;
  for (const id of METER_IDS) out[id] = seconds[id] * 1000;
  return out;
}

/** Animals with their own refill times; the others use the defaults. */
const REFILL_SECONDS_BY_SPECIES: Readonly<Record<string, Readonly<Record<MeterId, number>>>> = { shark: DEFAULT_REFILL_SECONDS };

export interface CareConfig {
  curves: Record<MeterId, CurveProfile>;
  refillSeconds: Record<MeterId, number>;
}

export type Validation = { ok: true } | { ok: false; error: string };

const fail = (error: string): Validation => ({ ok: false, error });
const oneDecimal = (n: number) => Math.abs(n * 10 - Math.round(n * 10)) < 1e-9;
const EPS = 1e-9;

export function validateCurveProfile(p: Partial<CurveProfile>): Validation {
  const keys = ["phase1End", "phase2End", "phase3End", "onePercentAt"] as const;
  for (const k of keys) {
    const v = p[k];
    if (typeof v !== "number" || !Number.isFinite(v)) return fail(`${k} must be a number`);
    if (!oneDecimal(v)) return fail(`${k} may have one decimal place`);
  }
  const { phase1End: a, phase2End: b, phase3End: c, onePercentAt: d } = p as CurveProfile;
  if (a < MIN_PHASE_HOURS - EPS) return fail("phase1End must be at least 0.1");
  if (b < a + MIN_PHASE_HOURS - EPS) return fail("phase2End must be at least 0.1 after phase1End");
  if (c < b + MIN_PHASE_HOURS - EPS) return fail("phase3End must be at least 0.1 after phase2End");
  if (d < c + MIN_PHASE_HOURS - EPS) return fail("onePercentAt must be at least 0.1 after phase3End");
  if (d < MIN_ONE_PERCENT_AT - EPS) return fail(`onePercentAt must be at least ${MIN_ONE_PERCENT_AT}`);
  if (d > MAX_ONE_PERCENT_AT + EPS) return fail(`onePercentAt must be at most ${MAX_ONE_PERCENT_AT}`);
  return { ok: true };
}

export function validateCareConfig(species: string, config: CareConfig): Validation {
  if (!KNOWN_SPECIES.includes(species)) return fail("unknown animal");
  if (typeof config !== "object" || config === null) return fail("bad config");
  for (const id of METER_IDS) {
    const curve = config.curves?.[id];
    if (typeof curve !== "object" || curve === null) return fail(`curve for ${id} is missing`);
    const v = validateCurveProfile(curve);
    if (!v.ok) return fail(`${id}: ${v.error}`);
    const s = config.refillSeconds?.[id];
    if (typeof s !== "number" || !Number.isInteger(s)) return fail(`refill for ${id} must be whole seconds`);
    if (s < MIN_REFILL_SECONDS || s > MAX_REFILL_SECONDS) return fail(`refill for ${id} must be ${MIN_REFILL_SECONDS} to ${MAX_REFILL_SECONDS} seconds`);
  }
  return { ok: true };
}
